import React, { useState } from "react";
import { useTrades, useCapital } from "../useJournal";
import { calculateBrokerage } from "../brokerage";

const s = {
  card: { background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: 20, marginBottom: 16 },
  label: { fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 3, display: "block", textTransform: "uppercase" },
  input: { width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 10px", fontSize: 13 },
  btn: (c) => ({ padding: "7px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12, background: c==="green"?"#16a34a":c==="red"?"#dc2626":c==="gray"?"#6b7280":"#1d4ed8", color: "white" }),
};

const fmt = (n,pre="₹") => { if(n==null||isNaN(n))return"—"; return pre+Number(n).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2}); };

const EMPTY = { symbol:"",exchange:"NSE",entry_date:new Date().toISOString().split("T")[0],entry_price:"",quantity:"",initial_sl:"",t1_price:"",t2_price:"",trade_type:"long",broker:"Zerodha",scan_type:"Setup",chart_url_daily:"",chart_url_weekly:"",notes:"",tag:"Breakout" };
const EMPTY_EXIT = { exit_date:new Date().toISOString().split("T")[0],exit_price:"",exit_quantity:"",exit_reason:"Target T1" };

export default function TradesPage() {
  const { trades, loading, addTrade, updateTrade, deleteTrade } = useTrades();
  const { totalCapital } = useCapital();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [exitForms, setExitForms] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  function setF(k,v) { setForm(f=>({...f,[k]:v})); }

  function calcTargets() {
    const entry = parseFloat(form.entry_price), sl = parseFloat(form.initial_sl);
    if (!entry||!sl) return;
    const risk = entry - sl;
    setForm(f=>({...f, t1_price:(entry+risk*2.5).toFixed(2), t2_price:(entry+risk*4.0).toFixed(2)}));
  }

  async function handleAdd() {
    if (!form.symbol||!form.entry_price||!form.quantity) return alert("Symbol, price and quantity required");
    setSaving(true);
    const qty=parseFloat(form.quantity),entry=parseFloat(form.entry_price),sl=parseFloat(form.initial_sl);
    const brok = calculateBrokerage(form.broker,"delivery",entry*qty,0,qty);
    await addTrade({ ...form, entry_price:entry, quantity:qty, initial_sl:sl, t1_price:parseFloat(form.t1_price), t2_price:parseFloat(form.t2_price), actual_risk:(entry-sl)*qty, entry_brokerage:brok[form.broker]?.total||0, exits:[], status:"open" });
    setForm(EMPTY); setShowForm(false); setSaving(false);
  }

  async function handleAddExit(tradeId) {
    const ef = exitForms[tradeId]||EMPTY_EXIT;
    if (!ef.exit_price||!ef.exit_quantity) return alert("Exit price and quantity required");
    const trade = trades.find(t=>t.id===tradeId);
    const exitQty=parseFloat(ef.exit_quantity),exitPrice=parseFloat(ef.exit_price);
    const brok = calculateBrokerage(trade.broker||"Zerodha","delivery",0,exitQty*exitPrice,exitQty);
    const brokerageTotal = brok[trade.broker]?.total||0;
    const newExit = { ...ef, exit_price:exitPrice, exit_quantity:exitQty, exit_brokerage:brokerageTotal, pnl:(exitPrice-trade.entry_price)*exitQty-brokerageTotal };
    const exits = [...(trade.exits||[]), newExit];
    const totalExitQty = exits.reduce((s,e)=>s+e.exit_quantity,0);
    await updateTrade(tradeId, { exits, status: totalExitQty>=trade.quantity?"closed":"open" });
    setExitForms(f=>({...f,[tradeId]:EMPTY_EXIT}));
  }

  const filtered = trades.filter(t => filter==="all"?true:filter==="open"?t.status==="open":t.status==="closed");

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ display:"flex", gap:8 }}>
          {["all","open","closed"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} style={{ padding:"5px 14px",borderRadius:5,border:"1px solid #d1d5db",fontWeight:600,fontSize:12,cursor:"pointer",textTransform:"capitalize",background:filter===f?"#1d4ed8":"white",color:filter===f?"white":"#374151" }}>{f} ({trades.filter(t=>f==="all"||t.status===f).length})</button>
          ))}
        </div>
        <button style={s.btn("green")} onClick={()=>setShowForm(!showForm)}>{showForm?"Cancel":"+ New Trade"}</button>
      </div>

      {showForm && (
        <div style={s.card}>
          <div style={{ fontWeight:700, marginBottom:16 }}>New Trade Entry</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            {[{k:"symbol",label:"Symbol",type:"text",placeholder:"RELIANCE"},{k:"entry_date",label:"Entry Date",type:"date"},{k:"entry_price",label:"Entry Price ₹",type:"number"},{k:"quantity",label:"Quantity",type:"number"},{k:"initial_sl",label:"Stop Loss ₹",type:"number"},{k:"t1_price",label:"T1 Price ₹",type:"number"},{k:"t2_price",label:"T2 Price ₹",type:"number"}].map(f=>(
              <div key={f.k}><label style={s.label}>{f.label}</label><input type={f.type} style={s.input} placeholder={f.placeholder} value={form[f.k]} onChange={e=>setF(f.k,e.target.value)} /></div>
            ))}
            <div style={{display:"flex",alignItems:"end"}}><button style={{...s.btn("gray"),width:"100%"}} onClick={calcTargets}>Auto T1/T2</button></div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginTop:12 }}>
            {[{k:"exchange",label:"Exchange",options:["NSE","BSE"]},{k:"trade_type",label:"Type",options:["long","short"]},{k:"broker",label:"Broker",options:["Zerodha","Dhan"]},{k:"scan_type",label:"Scan Type",options:["Setup","PPC","Shortlist","NPC","Manual"]},{k:"tag",label:"Tag",options:["Breakout","Pullback","Consolidation","Reversal"]}].map(f=>(
              <div key={f.k}><label style={s.label}>{f.label}</label><select style={s.input} value={form[f.k]} onChange={e=>setF(f.k,e.target.value)}>{f.options.map(o=><option key={o}>{o}</option>)}</select></div>
            ))}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
            <div><label style={s.label}>Daily Chart URL</label><input type="text" style={s.input} placeholder="https://www.tradingview.com/x/..." value={form.chart_url_daily} onChange={e=>setF("chart_url_daily",e.target.value)} /></div>
            <div><label style={s.label}>Weekly Chart URL</label><input type="text" style={s.input} placeholder="https://www.tradingview.com/x/..." value={form.chart_url_weekly} onChange={e=>setF("chart_url_weekly",e.target.value)} /></div>
          </div>
          <div style={{marginTop:12}}><label style={s.label}>Notes</label><textarea style={{...s.input,height:60}} value={form.notes} onChange={e=>setF("notes",e.target.value)} /></div>
          {form.entry_price&&form.quantity&&(
            <div style={{marginTop:12,padding:"10px 12px",background:"#f9fafb",borderRadius:6,fontSize:12}}>
              Brokerage ({form.broker}): ₹{calculateBrokerage(form.broker,"delivery",parseFloat(form.entry_price)*parseFloat(form.quantity),0,parseFloat(form.quantity))[form.broker]?.total||0} estimated on buy side
            </div>
          )}
          <div style={{marginTop:16,display:"flex",gap:8}}>
            <button style={s.btn("green")} onClick={handleAdd} disabled={saving}>{saving?"Saving...":"Save Trade"}</button>
            <button style={s.btn("gray")} onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{color:"#9ca3af",padding:20}}>Loading...</div> : filtered.length===0 ? (
        <div style={{...s.card,textAlign:"center",padding:40,color:"#9ca3af"}}>No trades yet. Click "+ New Trade" to add your first trade.</div>
      ) : filtered.map(trade=>{
        const isOpen = trade.status==="open";
        const totalExitQty = (trade.exits||[]).reduce((s,e)=>s+e.exit_quantity,0);
        const remaining = trade.quantity - totalExitQty;
        const totalPnL = (trade.exits||[]).reduce((s,e)=>s+e.pnl,0);
        const rr = (trade.entry_price-trade.initial_sl)>0?((trade.t1_price-trade.entry_price)/(trade.entry_price-trade.initial_sl)).toFixed(1):"—";
        const ef = exitForms[trade.id]||EMPTY_EXIT;

        return (
          <div key={trade.id} style={{...s.card,borderLeft:`4px solid ${isOpen?"#1d4ed8":"#16a34a"}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}} onClick={()=>setExpanded(expanded===trade.id?null:trade.id)}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontWeight:800,fontSize:16}}>{trade.symbol}</span>
                <span style={{fontSize:11,padding:"2px 7px",borderRadius:4,fontWeight:600,background:isOpen?"#dbeafe":"#dcfce7",color:isOpen?"#1d4ed8":"#16a34a"}}>{isOpen?"OPEN":"CLOSED"}</span>
                <span style={{fontSize:11,color:"#6b7280"}}>{trade.tag}</span>
              </div>
              <div style={{display:"flex",gap:20,fontSize:13}}>
                <span>Entry: <strong>{fmt(trade.entry_price)}</strong></span>
                <span>Qty: <strong>{trade.quantity}</strong></span>
                <span>SL: <strong style={{color:"#dc2626"}}>{fmt(trade.initial_sl)}</strong></span>
                <span>RR: <strong>{rr}x</strong></span>
                {!isOpen&&<span style={{color:totalPnL>=0?"#16a34a":"#dc2626",fontWeight:700}}>P&L: {fmt(totalPnL)}</span>}
                <span style={{color:"#9ca3af"}}>{expanded===trade.id?"▲":"▼"}</span>
              </div>
            </div>

            {expanded===trade.id&&(
              <div style={{marginTop:16}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
                  {[{label:"Entry Date",value:trade.entry_date},{label:"Exchange",value:trade.exchange},{label:"Broker",value:trade.broker},{label:"T1",value:fmt(trade.t1_price)},{label:"T2",value:fmt(trade.t2_price)},{label:"Actual Risk",value:fmt(trade.actual_risk)},{label:"Remaining Qty",value:remaining},{label:"Entry Brokerage",value:fmt(trade.entry_brokerage)}].map(f=>(
                    <div key={f.label}><div style={{fontSize:10,color:"#9ca3af",fontWeight:600,textTransform:"uppercase"}}>{f.label}</div><div style={{fontSize:13,fontWeight:600}}>{f.value}</div></div>
                  ))}
                </div>

                <div style={{display:"flex",gap:12,marginBottom:12}}>
                  {trade.chart_url_daily&&<a href={trade.chart_url_daily} target="_blank" rel="noreferrer" style={{fontSize:12,color:"#1d4ed8"}}>📈 Daily Chart</a>}
                  {trade.chart_url_weekly&&<a href={trade.chart_url_weekly} target="_blank" rel="noreferrer" style={{fontSize:12,color:"#1d4ed8"}}>📊 Weekly Chart</a>}
                </div>

                {(trade.exits||[]).length>0&&(
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>Exits</div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead><tr style={{borderBottom:"1px solid #e5e7eb"}}>{["Date","Price","Qty","Reason","Brokerage","P&L"].map(h=><th key={h} style={{padding:"4px 8px",textAlign:"left",color:"#6b7280",fontWeight:600,fontSize:11}}>{h}</th>)}</tr></thead>
                      <tbody>
                        {trade.exits.map((e,i)=>(
                          <tr key={i} style={{borderBottom:"1px solid #f3f4f6"}}>
                            <td style={{padding:"6px 8px"}}>{e.exit_date}</td>
                            <td style={{padding:"6px 8px",fontFamily:"monospace"}}>{fmt(e.exit_price)}</td>
                            <td style={{padding:"6px 8px"}}>{e.exit_quantity}</td>
                            <td style={{padding:"6px 8px"}}>{e.exit_reason}</td>
                            <td style={{padding:"6px 8px",fontFamily:"monospace"}}>{fmt(e.exit_brokerage)}</td>
                            <td style={{padding:"6px 8px",fontFamily:"monospace",fontWeight:700,color:e.pnl>=0?"#16a34a":"#dc2626"}}>{fmt(e.pnl)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {isOpen&&remaining>0&&(
                  <div style={{background:"#f9fafb",padding:12,borderRadius:6}}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>Add Exit</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr) auto",gap:10,alignItems:"end"}}>
                      <div><label style={s.label}>Exit Date</label><input type="date" style={s.input} value={ef.exit_date} onChange={e=>setExitForms(f=>({...f,[trade.id]:{...(f[trade.id]||EMPTY_EXIT),exit_date:e.target.value}}))} /></div>
                      <div><label style={s.label}>Exit Price ₹</label><input type="number" style={s.input} value={ef.exit_price} onChange={e=>setExitForms(f=>({...f,[trade.id]:{...(f[trade.id]||EMPTY_EXIT),exit_price:e.target.value}}))} /></div>
                      <div><label style={s.label}>Qty (max {remaining})</label><input type="number" style={s.input} value={ef.exit_quantity} onChange={e=>setExitForms(f=>({...f,[trade.id]:{...(f[trade.id]||EMPTY_EXIT),exit_quantity:e.target.value}}))} /></div>
                      <div><label style={s.label}>Reason</label><select style={s.input} value={ef.exit_reason} onChange={e=>setExitForms(f=>({...f,[trade.id]:{...(f[trade.id]||EMPTY_EXIT),exit_reason:e.target.value}}))}>
                        {["Target T1","Target T2","Stop Loss","Trailing Stop","Partial Profit","Manual"].map(o=><option key={o}>{o}</option>)}
                      </select></div>
                      <button style={s.btn("red")} onClick={()=>handleAddExit(trade.id)}>Exit</button>
                    </div>
                  </div>
                )}

                {trade.notes&&<div style={{marginTop:12,fontSize:12,color:"#6b7280",fontStyle:"italic"}}>Note: {trade.notes}</div>}
                <div style={{marginTop:12}}><button onClick={()=>deleteTrade(trade.id)} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer"}}>Delete trade</button></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
